import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { FaDownload, FaEye, FaEyeSlash } from 'react-icons/fa';

import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import { useSnackbar } from 'v3/components/Snackbar';
import usePermission from 'hooks/usePermission';
import {
  getShippingCompanyCertificatePassword,
  getShippingCompanyCertificateStatus,
  getShippingCompanyCertificateUrl,
} from 'v3/services/shippingCompany';

import SharedData from './Section';

const NO_CERTIFICATE = 'Transportadora sem certificado digital cadastrado';
const NO_PASSWORD = 'Transportadora sem senha de certificado cadastrada';

export default function CertificateCard({ personId, loading = false }) {
  const snackbar = useSnackbar();

  const canDownload = usePermission('VISUALIZAR_CERTIFICADO_TRANSPORTADORA');
  const canViewPassword = usePermission('VISUALIZAR_SENHA_CERTIFICADO');

  const [downloading, setDownloading] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [password, setPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  // { hasCertificate, hasPassword }: null = sem permissão ou ainda sem resposta
  const [certificateStatus, setCertificateStatus] = useState({
    hasCertificate: null,
    hasPassword: null,
  });

  const canSeeAny = canDownload || canViewPassword;

  useEffect(() => {
    setCertificateStatus({ hasCertificate: null, hasPassword: null });
    setPassword(null);
    setShowPassword(false);
    if (!personId || !canSeeAny) return undefined;

    let ignore = false;
    getShippingCompanyCertificateStatus(personId)
      .then((data) => {
        if (!ignore) {
          setCertificateStatus({
            hasCertificate: data?.hasCertificate ?? null,
            hasPassword: data?.hasPassword ?? null,
          });
        }
      })
      // Sem a situação, os botões ficam como antes; o 404 no clique mostra o aviso.
      .catch(() => {});

    return () => {
      ignore = true;
    };
  }, [personId, canSeeAny]);

  if (!canSeeAny) {
    return null;
  }

  async function handleDownload() {
    if (!personId) return;
    setDownloading(true);
    try {
      const data = await getShippingCompanyCertificateUrl(personId);

      const link = document.createElement('a');
      link.href = data.url;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      if (err.status === 404) {
        setCertificateStatus((prev) => ({ ...prev, hasCertificate: false }));
      } else {
        snackbar.show(<Text>{err.message}</Text>, { type: 'error' });
      }
    } finally {
      setDownloading(false);
    }
  }

  async function handleViewPassword() {
    if (password !== null) {
      setShowPassword((prev) => !prev);
      return;
    }
    if (!personId) return;
    setLoadingPassword(true);
    try {
      const data = await getShippingCompanyCertificatePassword(personId);
      setPassword(data.password);
      setShowPassword(true);
    } catch (err) {
      if (err.status === 404) {
        setCertificateStatus((prev) => ({ ...prev, hasPassword: false }));
      } else {
        snackbar.show(<Text>{err.message}</Text>, { type: 'error' });
      }
    } finally {
      setLoadingPassword(false);
    }
  }

  return (
    <SharedData Title="Certificado Digital" loading={loading}>
      <Row className="mt-2">
        {canDownload && (
          <Col xs={12} className="mb-3">
            {certificateStatus.hasCertificate === false ? (
              <Text type="regular" color="gray" data-testid="carrier-no-certificate">
                {NO_CERTIFICATE}
              </Text>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                loading={downloading}
                disabled={downloading || !personId}
                onClick={handleDownload}
              >
                <FaDownload style={{ marginRight: '0.4rem' }} />
                <Text color="dark" weight={500} type="regular">
                  Baixar certificado
                </Text>
              </Button>
            )}
          </Col>
        )}

        {canViewPassword && (
          <Col xs={12}>
            <Text type="label" color="dark">
              Senha do certificado
            </Text>
            {certificateStatus.hasPassword === false ? (
              <Text
                type="regular"
                color="gray"
                as="p"
                className="mt-1"
                data-testid="carrier-no-certificate-password"
              >
                {NO_PASSWORD}
              </Text>
            ) : (
              <div className="d-flex align-items-center mt-1" style={{ gap: '0.5rem' }}>
                <Text type="regular" color="#494950">
                  {password !== null && showPassword ? password : '••••••••'}
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={loadingPassword}
                  disabled={loadingPassword || !personId}
                  onClick={handleViewPassword}
                  style={{ padding: '0.25rem 0.5rem' }}
                >
                  {showPassword && password !== null ? (
                    <FaEyeSlash size={14} />
                  ) : (
                    <FaEye size={14} />
                  )}
                </Button>
              </div>
            )}
          </Col>
        )}
      </Row>
    </SharedData>
  );
}
